import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { getCompanyEmployees } from '../../api';
import { assignCRMContact } from '../api';
import { syncPipelineBoardContact } from '../utils/pipelineSync';
import { useMobileScrollLock } from '@/common/hooks';
import toast from 'react-hot-toast';
import { parseApiError } from '@/common/utils';

function getEmployeeLabel(employee) {
  if (!employee) return '';
  return (
    employee.full_name ||
    employee.name ||
    [employee.first_name, employee.last_name].filter(Boolean).join(' ') ||
    employee.email ||
    `Employee ${employee.id}`
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      className={`w-4 h-4 flex-shrink-0 transition-transform text-[#50AB3D] ${open ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

export function InlineEmployeeSelector({
  companyId,
  contactId,
  employeeId,
  employeeName,
  disabled = false,
  variant = 'input', // 'input' | 'card' | 'list'
  className = '',
}) {
  const queryClient = useQueryClient();
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const rollbackRef = useRef({ id: null, name: null });
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [optimisticId, setOptimisticId] = useState(null);
  const [optimisticName, setOptimisticName] = useState(null);
  const [dropdownStyle, setDropdownStyle] = useState({
    position: 'fixed',
    top: -9999,
    left: -9999,
  });

  const isPopoverVariant = variant === 'card' || variant === 'list';

  const resolvedCompanyId =
    companyId ||
    localStorage.getItem('company_id') ||
    sessionStorage.getItem('company_id') ||
    (() => {
      try {
        const u = JSON.parse(
          localStorage.getItem('user') ||
            sessionStorage.getItem('user') ||
            '{}',
        );
        return u?.company_id || u?.company?.id || u?.id || '';
      } catch {
        return '';
      }
    })();

  useEffect(() => {
    setOptimisticId(null);
    setOptimisticName(null);
  }, [employeeId, employeeName, contactId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Focus search input when popover dropdown opens
  useEffect(() => {
    if (isOpen && isPopoverVariant) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isPopoverVariant]);

  useMobileScrollLock(isOpen);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['companyEmployees-search', resolvedCompanyId, debouncedSearch],
      queryFn: ({ pageParam = 1 }) => {
        const params = debouncedSearch
          ? { search: debouncedSearch, page: pageParam }
          : { page: pageParam };
        return getCompanyEmployees(resolvedCompanyId, params);
      },
      getNextPageParam: (lastPage) => {
        if (!lastPage?.next) return undefined;
        try {
          const url = new URL(lastPage.next);
          return url.searchParams.get('page') || undefined;
        } catch {
          const match = String(lastPage.next).match(/[?&]page=(\d+)/);
          return match ? match[1] : undefined;
        }
      },
      enabled: !!resolvedCompanyId && isOpen,
    });

  const employees = useMemo(
    () =>
      data?.pages?.flatMap((page) =>
        Array.isArray(page) ? page : page?.results || [],
      ) || [],
    [data],
  );

  const cachedEmployees = useMemo(() => {
    if (employees.length > 0) return employees;
    const filterCache = queryClient.getQueryData([
      'companyEmployees',
      resolvedCompanyId,
      'pipeline-filter',
    ]);
    if (Array.isArray(filterCache)) return filterCache;
    if (Array.isArray(filterCache?.results)) return filterCache.results;
    return [];
  }, [employees, queryClient, resolvedCompanyId]);

  const currentId = optimisticId ?? employeeId ?? '';

  const matchedEmp = useMemo(() => {
    if (!currentId && !employeeName) return null;
    return cachedEmployees.find(
      (e) =>
        (currentId && String(e.id) === String(currentId)) ||
        (employeeName &&
          (String(e.id) === String(employeeName) ||
            String(e.phone) === String(employeeName) ||
            String(e.email) === String(employeeName))),
    );
  }, [cachedEmployees, currentId, employeeName]);

  const rawName =
    optimisticName ??
    employeeName ??
    (currentId ? `Employee #${currentId}` : '');

  // If rawName is purely digits/phone (e.g. "123456789"), prioritize the employee's actual name
  const displayName =
    optimisticName ??
    (rawName && !/^\d+$/.test(String(rawName).trim()) ? rawName : null) ??
    (matchedEmp ? getEmployeeLabel(matchedEmp) : null) ??
    rawName;

  const assignMutation = useMutation({
    mutationFn: assignCRMContact,
    onSuccess: (_data, variables) => {
      toast.success('Employee assigned successfully!');
      queryClient.invalidateQueries({
        queryKey: ['crm-contact-detail', resolvedCompanyId, contactId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-contact-timeline', resolvedCompanyId, contactId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-contacts'],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-pipeline-board'],
      });
      syncPipelineBoardContact(queryClient, contactId, {
        assigned_to_employee_id: variables?.payload?.assignee_id,
        assigned_to_id: variables?.payload?.assignee_id,
        assigned_to_employee_name: optimisticName || displayName,
        assigned_to_name: optimisticName || displayName,
      });
    },
    onError: (error) => {
      setOptimisticId(rollbackRef.current.id);
      setOptimisticName(rollbackRef.current.name);
      const { message } = parseApiError(error);
      toast.error(message || 'Failed to assign employee.');
    },
  });

  const openDropdown = () => {
    if (disabled || assignMutation.isPending) return;
    setSearchQuery('');
    setDebouncedSearch('');
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchQuery('');
    setDebouncedSearch('');
  };

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (!isOpen || !triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placement =
        spaceBelow < 280 && spaceAbove > spaceBelow ? 'top' : 'bottom';
      const dropdownWidth = isPopoverVariant ? 260 : Math.max(rect.width, 240);
      const left = Math.max(
        8,
        Math.min(rect.left, window.innerWidth - dropdownWidth - 8),
      );
      setDropdownStyle({
        position: 'fixed',
        left,
        width: dropdownWidth,
        zIndex: 99999,
        ...(placement === 'bottom'
          ? { top: rect.bottom + 4 }
          : { bottom: window.innerHeight - rect.top + 4 }),
      });
    };

    updatePosition();
    if (!isOpen) return undefined;
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, isPopoverVariant]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        dropdownRef.current?.contains(event.target)
      ) {
        return;
      }
      closeDropdown();
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [isOpen]);

  const handleSelect = (emp) => {
    const newId = emp.id;
    if (String(newId) === String(currentId)) {
      closeDropdown();
      return;
    }
    const nextName = getEmployeeLabel(emp);
    rollbackRef.current = { id: currentId, name: displayName };
    setOptimisticId(newId);
    setOptimisticName(nextName);
    closeDropdown();
    assignMutation.mutate({
      companyId: resolvedCompanyId,
      contactId,
      payload: { assignee_id: newId },
    });
  };

  const handleListScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 30) {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    }
  };

  const isPending = assignMutation.isPending;

  return (
    <>
      {variant === 'card' ? (
        /* Kanban Card Variant Trigger */
        <div
          ref={triggerRef}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled || isPending) return;
            if (isOpen) closeDropdown();
            else openDropdown();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          data-no-card-drag
          className={`flex items-center gap-2 min-w-0 cursor-pointer rounded-lg px-2 py-1 bg-[#F5F5F7] hover:bg-[#ECECED] active:bg-[#E2E2E5] border border-transparent hover:border-gray-200 transition-colors select-none group/assignee ${
            disabled || isPending ? 'opacity-70 cursor-not-allowed' : ''
          } ${className}`}
          title={
            displayName
              ? `${displayName} - Click to change assignee`
              : 'Click to change assignee'
          }
        >
          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 relative">
            {isPending ? (
              <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            ) : (
              <span className="text-[11px] font-bold text-primary">
                {(displayName || 'U').charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <span className="truncate max-w-[120px] font-medium text-[#1F2241] text-xs group-hover/assignee:text-primary transition-colors">
            {displayName || 'Unassigned'}
          </span>
        </div>
      ) : variant === 'list' ? (
        /* List View Row Variant Trigger */
        <div
          ref={triggerRef}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled || isPending) return;
            if (isOpen) closeDropdown();
            else openDropdown();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          data-no-card-drag
          className={`flex items-center gap-1.5 min-w-0 cursor-pointer rounded-lg px-2 py-1 bg-[#F5F5F7] hover:bg-[#ECECED] active:bg-[#E2E2E5] border border-transparent hover:border-gray-200 transition-colors select-none group/assignee max-w-full ${
            disabled || isPending ? 'opacity-70 cursor-not-allowed' : ''
          } ${className}`}
          title={
            displayName
              ? `${displayName} - Click to change assignee`
              : 'Click to change assignee'
          }
        >
          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 relative">
            {isPending ? (
              <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            ) : (
              <span className="text-[10px] font-bold text-primary">
                {(displayName || 'U').charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <span className="truncate text-xs font-medium text-[#1F2241] group-hover/assignee:text-primary transition-colors">
            {displayName || 'Unassigned'}
          </span>
        </div>
      ) : (
        /* Standard Input Variant Trigger (Contact Detail Page) */
        <div
          className={`relative flex items-center w-full ${className}`}
          ref={triggerRef}
        >
          <input
            type="text"
            value={isOpen ? searchQuery : displayName}
            onChange={(e) => {
              if (!isOpen) openDropdown();
              setSearchQuery(e.target.value);
            }}
            onFocus={openDropdown}
            placeholder="Search and select user..."
            className={`w-full h-11 px-4 pr-10 rounded-xl bg-[#FFFFFF] border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors cursor-text ${
              disabled || isPending
                ? 'border-border-default opacity-60 cursor-not-allowed'
                : isOpen
                  ? 'border-[#50AB3D]'
                  : 'border-border-default hover:border-[#50AB3D]'
            }`}
            disabled={disabled || isPending}
            autoComplete="off"
          />
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (isOpen) closeDropdown();
              else openDropdown();
            }}
            className="absolute right-0 top-0 bottom-0 px-3.5 flex items-center justify-center cursor-pointer text-[#50AB3D] focus:outline-none z-10"
            aria-label="Toggle user dropdown"
            disabled={disabled || isPending}
          >
            <ChevronIcon open={isOpen} />
          </button>
        </div>
      )}

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{ ...dropdownStyle, overscrollBehavior: 'contain' }}
            data-portal-dropdown="true"
            data-no-card-drag
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="z-[99999] bg-white border border-border-default rounded-xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150"
          >
            {/* Search Input at Top for Card/List Popover Variant */}
            {isPopoverVariant && (
              <div className="p-2.5 border-b border-border-default bg-[#FAFAFA] shrink-0">
                <div className="relative flex items-center">
                  <input
                    type="text"
                    ref={searchInputRef}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    placeholder="Search employee..."
                    className="w-full h-8 pl-8 pr-7 text-xs bg-white border border-border-default rounded-lg focus:outline-none focus:border-[#50AB3D] text-[#1F2241] placeholder:text-[#868686] transition-colors"
                  />
                  <svg
                    className="w-3.5 h-3.5 text-[#868686] absolute left-2.5 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchQuery('');
                        searchInputRef.current?.focus();
                      }}
                      className="absolute right-2 text-[#868686] hover:text-[#1F2241] text-xs p-1"
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Scrollable Employee List */}
            <div
              className="max-h-56 overflow-y-auto custom-scrollbar py-1 divide-y divide-gray-50"
              onScroll={handleListScroll}
            >
              {isLoading ? (
                <div className="p-4 text-center text-xs text-[#868686] flex items-center justify-center gap-2">
                  <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  <span>Loading employees...</span>
                </div>
              ) : employees.length > 0 ? (
                <>
                  {employees.map((emp) => {
                    const label = getEmployeeLabel(emp);
                    const isSelected = String(currentId) === String(emp.id);
                    return (
                      <button
                        key={emp.id}
                        type="button"
                        className={`w-full flex items-center gap-2.5 text-left px-3 py-2 transition-colors ${
                          isSelected
                            ? 'bg-[#EBF4EA] text-[#50AB3D]'
                            : 'bg-white hover:bg-[#F5F5F5] text-[#1F2241]'
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(emp);
                        }}
                      >
                        <span
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                            isSelected
                              ? 'bg-[#50AB3D] text-white'
                              : 'bg-[#EBF4EA] text-[#50AB3D]'
                          }`}
                        >
                          {(label || '?').charAt(0).toUpperCase()}
                        </span>
                        <span className="min-w-0 flex flex-col flex-1">
                          <span
                            className={`text-xs font-medium truncate ${
                              isSelected
                                ? 'text-[#50AB3D] font-semibold'
                                : 'text-[#1F2241]'
                            }`}
                          >
                            {label}
                          </span>
                          {emp.email && (
                            <span className="text-[11px] text-[#868686] truncate">
                              {emp.email}
                            </span>
                          )}
                        </span>
                        {isSelected && (
                          <svg
                            className="w-4 h-4 text-[#50AB3D] shrink-0"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M4.5 12.75l6 6 9-13.5"
                            />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                  {isFetchingNextPage && (
                    <div className="p-2 text-center text-xs text-[#868686] flex items-center justify-center gap-2">
                      <div className="w-3 h-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                      <span>Loading more...</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-4 text-center text-xs text-[#868686]">
                  No employees found
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
